/**
 * Auth event handler - manages auth events across app
 */

import { RankingsService } from '../rankings/services/RankingsService.js';
import { clearBlockedContent, refreshBlockedContent } from '../moderation/blockedContentStore.js';

const AUTH_EVENTS = {
  TOKEN_EXPIRED: 'auth:token:expired',
  LOGOUT: 'auth:logout',
  LOGIN_SUCCESS: 'auth:login:success',
  AUTH_REQUIRED: 'authRequired',
  SHOW_LOGIN: 'auth:show-login-modal',
  GLOBAL_LOGIN: 'global:login',
  GLOBAL_LOGOUT: 'global:logout'
};

export function initAuthEventHandler() {
  Object.values(AUTH_EVENTS).forEach(event => {
    if (event === AUTH_EVENTS.GLOBAL_LOGIN || event === AUTH_EVENTS.GLOBAL_LOGOUT) return;
    document.addEventListener(event, getHandler(event));
  });
}

function getHandler(eventType) {
  const handlers = {
    [AUTH_EVENTS.TOKEN_EXPIRED]: handleTokenExpired,
    [AUTH_EVENTS.LOGOUT]: handleLogout,
    [AUTH_EVENTS.LOGIN_SUCCESS]: handleLoginSuccess,
    [AUTH_EVENTS.AUTH_REQUIRED]: handleAuthRequired
  };
  return handlers[eventType];
}

function handleTokenExpired() {
  // Persistence is cleared in TokenManager.clearTokens via clearPersistedClientData
  dispatchGlobalEvent('global:login-required', { reason: 'session_expired' });
}

function handleLogout(event) {
  clearBlockedContent();
  void clearRankingsUserScopedCache();
  dispatchGlobalEvent(AUTH_EVENTS.GLOBAL_LOGOUT, event.detail);
}

function handleLoginSuccess(event) {
  clearRankingsPrivateCache();
  void refreshBlockedContent();
  dispatchGlobalEvent(AUTH_EVENTS.GLOBAL_LOGIN, event.detail);
}

function handleAuthRequired(event) {
  const { reason, message } = event.detail || {};
  // Passive mode: don't auto-open login modal globally
  // Consumers can listen for this global event and present UI when appropriate
  dispatchGlobalEvent('global:login-required', { reason: reason || 'auth_required', message });
}

function showLoginModal(reason, message) {
  // Try multiple modal access methods
  const modalMethods = [
    () => window.showLoginModal && window.showLoginModal(),
    () => window.loginModalController && window.loginModalController.show && window.loginModalController.show(),
    () => {
      const modal = document.getElementById('loginModal');
      if (modal) {
        modal.style.display = 'flex';
      }
    },
    () => dispatchGlobalEvent(AUTH_EVENTS.SHOW_LOGIN, { reason, message })
  ];

  for (const method of modalMethods) {
    try {
      const result = method();
      const modal = document.getElementById('loginModal');
      if (result !== undefined || (modal && modal.style.display === 'flex')) {
        return true;
      }
    } catch (error) {
      // Silent catch
    }
  }
  return false;
}

function redirectOnError(error) {
  setTimeout(() => {
    if (!window.location.pathname.includes('rankings.html')) {
      window.location.href = `/rankings.html?error=${error}`;
    }
  }, 1000);
}

function dispatchGlobalEvent(eventName, detail) {
  document.dispatchEvent(new CustomEvent(eventName, { detail }));
}

async function clearRankingsUserScopedCache() {
  try {
    const rankingsService = RankingsService.getInstance();
    await rankingsService.clearUserScopedCacheOnLogout();
  } catch (error) {
    console.warn('[AuthEventHandler] Failed to clear rankings cache on logout:', error);
  }

  try {
    if (window.navigation?.currentUser) {
      window.navigation.currentUser = undefined;
    }
  } catch (error) {
    console.warn('[AuthEventHandler] Failed to clear navigation current user:', error);
  }
}

function clearRankingsPrivateCache() {
  try {
    const rankingsService = RankingsService.getInstance();
    rankingsService.clearPrivateCharactersCache();
  } catch (error) {
    console.warn('[AuthEventHandler] Failed to clear rankings private cache on login:', error);
  }
}

export function cleanupAuthEventHandler() {
  Object.values(AUTH_EVENTS).forEach(event => {
    if (event === AUTH_EVENTS.GLOBAL_LOGIN || event === AUTH_EVENTS.GLOBAL_LOGOUT) return;
    document.removeEventListener(event, getHandler(event));
  });
}
